"use client";

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { sourced } from "@/components/ui/transaction-review-contract";
import { confirmOfferStep } from "@/features/finance/api";
import { classifyChainPost } from "@/features/finance/chain-settlement";
import { trackChainWork } from "@/features/finance/chain-work-dock";
import { currencyStateReason, useCurrencyBalance, useNetworkCurrencies } from "@/features/finance/currency-balance";
import { useExternalStepHandoff } from "@/features/finance/hooks/useExternalStepHandoff";
import { refreshQuietly, submitPlaceOffer } from "@/features/finance/place-offer";
import { useUserBalance } from "@/features/wallet/hooks/useUserBalance";
import { isShort, spendableOn } from "@/features/wallet/spendable";
import { toastApiError } from "@/lib/api-toast";
import { getErrorMessage } from "@/lib/error-message";
import { divideDecimalString, formatDecimal, isPositive, parseDecimal, parseIntegerInput } from "@/lib/decimal-amount";
import { formatDecimalString } from "@/lib/token-amount";
import type { TransactionRecord } from "@/lib/transaction-flow";
import { buildReviewRows } from "../review-rows";
import { formatUnixDate, type FlowModel, type SharedFlowProps } from "./shared";
export const DURATIONS = ["1", "3", "7", "30"] as const;
export type PlaceOfferProps = SharedFlowProps & {
  fallbackSymbol: string;
  networkId?: number;
  /** The live listing's price per item — an offer may not be below it (the backend refuses with 400). Null when not listed. */
  listingFloor?: number | null;
  listingFloorSymbol?: string | null;
  onClose: () => void;
  onSuccess: () => Promise<void>;
};

/**
 * PLACE AN OFFER — the legacy `PlaceOfferFlow`, moved verbatim: the entered
 * amount IS the total (`total_price`), the per-item figure derived and
 * labelled so; the deadline fixed when the review is built; the amount's own
 * error shown only after interaction; the decision in `submitPlaceOffer`, so a
 * refetch that fails never turns a placed offer into a failed one; the
 * external wallet's wrap / approve / sign rounds against the one `offer_id`;
 * the offers list behind is the acknowledgement (the dialog closes).
 */
export function usePlaceOffer(p: PlaceOfferProps) {
  const {
    lang,
    assetName,
    assetImage,
    collectionName,
    tokenId,
    networkLabel,
    royaltyPercent,
    contractAddress,
    onRecord,
    fallbackSymbol,
    networkId,
    onClose,
    onSuccess,
    listingFloor = null,
    listingFloorSymbol = null
  } = p;
  const isFa = lang === "fa";
  const [amount, setAmount] = useState("");
  // Only for deciding whether the amount field may show its own error yet.
  const [amountTouched, setAmountTouched] = useState(false);
  const [quantity, setQuantity] = useState("1");
  const [duration, setDuration] = useState<string>("7");
  const [selectedCurrencyId, setSelectedCurrencyId] = useState("");
  // The longest sequence of the five: wrap POL into WPOL, approve it, sign the bid.
  const handoff = useExternalStepHandoff(lang, "offer_id");
  const currencyState = useNetworkCurrencies(networkId);
  const {
    currencies
  } = currencyState;
  useEffect(() => {
    if (currencies.length === 1) {
      setSelectedCurrencyId(String(currencies[0].id));
      return;
    }
    setSelectedCurrencyId(current => current && !currencies.some(c => String(c.id) === current) ? "" : current);
  }, [currencies]);
  const currency = currencies.find(c_0 => String(c_0.id) === selectedCurrencyId);
  const balanceState = useCurrencyBalance(currency, lang);
  const activeSymbol = currency?.symbol?.toUpperCase() ?? fallbackSymbol;
  const parsedAmount = parseDecimal(amount);
  const amountValid = Boolean(parsedAmount && isPositive(parsedAmount));
  const canonicalAmount = amountValid && parsedAmount ? formatDecimal(parsedAmount) : null;
  const parsedQuantity = parseIntegerInput(quantity);
  const quantityValid = parsedQuantity !== null && parsedQuantity >= 1;
  const quantityNumber = parsedQuantity ?? 0;

  // `placeOffer` sends `total_price`, so the entered amount IS the total.
  const total = canonicalAmount;
  const perItem = canonicalAmount && quantityValid ? divideDecimalString(canonicalAmount, quantityNumber, 18) : null;

  // Fixed when the review is built, so the number reviewed is the number submitted.
  const deadline = useMemo(() => Math.floor(Date.now() / 1000) + Number(duration) * 86400, [duration]);

  // The backend's rule, checked before sending: with a live listing, the offer's total may not be below its price per item.
  const floor = listingFloor !== null && Number.isFinite(listingFloor) && listingFloor > 0 ? listingFloor : null;
  const belowFloor = amountValid && floor !== null && Number(canonicalAmount) < floor;
  const floorText = floor !== null ? `${formatDecimalString(String(floor), {
    locale: lang,
    maxFractionDigits: 18
  })}${listingFloorSymbol ? ` ${listingFloorSymbol.toUpperCase()}` : ""}` : "";
  const amountError = !amountValid && (amount || amountTouched) ? amount ? isFa ? "مبلغ باید عددی بزرگ‌تر از صفر باشد." : "The amount must be a number greater than zero." : isFa ? "برای ادامه، مبلغی بزرگ‌تر از صفر وارد کنید." : "Enter an amount greater than zero to continue." : belowFloor ? isFa ? `مبلغ پیشنهاد باید مساوی یا بیشتر از قیمت لیست این NFT باشد (${floorText}).` : `The offer must be equal to or greater than this NFT's listing price (${floorText}).` : undefined;
  const quantityError = quantity && !quantityValid ? isFa ? "تعداد باید عددی صحیح و حداقل ۱ باشد." : "Quantity must be a whole number of at least 1." : undefined;

  // What the wallet can spend of this currency on the NFT's network (locked excluded; a wrapped coin counts its coin).
  // An offer above it is refused by the backend, so it is stopped here, with the way to top up beside it.
  const {
    wallets
  } = useUserBalance();
  const spend = spendableOn(wallets, networkId, currency);
  const short = amountValid && !belowFloor && isShort(canonicalAmount, spend);

  // Continue is blocked by any of these, regardless of where its reason is shown.
  const isBlocked = !amountValid || belowFloor || !quantityValid || !currency || short;
  // The amount's reason renders with the amount field; only the others are stated in the footer.
  const blockedReason = !amountValid || belowFloor ? null : !quantityValid ? isFa ? "برای ادامه، تعداد را درست وارد کنید." : "Enter a valid quantity to continue." : !currency ? currencyStateReason(currencyState, isFa) ?? (isFa ? "برای ادامه یک ارز انتخاب کنید." : "Select a currency to continue.") : null; // A shortfall is said by the top-up notice beside the amount, not a second time here.

  const rows = buildReviewRows({
    lang,
    perspective: "buyer",
    assetName,
    collectionName,
    tokenId,
    quantity: quantityValid ? quantityNumber : null,
    unitPrice: perItem?.text ?? null,
    unitPriceRounded: perItem ? !perItem.exact : false,
    total,
    currencySymbol: currency?.symbol?.toUpperCase() ?? null,
    currencyUnavailableReason: currencyStateReason(currencyState, isFa) ?? undefined,
    networkLabel,
    balance: balanceState.balance,
    balanceUnavailableReason: balanceState.reason,
    royaltyPercent,
    expiration: sourced(formatUnixDate(deadline, lang) ?? (isFa ? "نامشخص" : "Unknown"), isFa ? "مهلت آفر که همراه درخواست ارسال می‌شود." : "The offer deadline submitted with the request."),
    conditions: sourced(isFa ? "آفر تا زمان پذیرش، لغو یا انقضا باز می‌ماند. پذیرش آن در اختیار مالک است." : "The offer stays open until it is accepted, cancelled or expires. Accepting it is the owner's decision.")
  });
  const onConfirm = async (requestId: string): Promise<TransactionRecord | null> => {
    if (!currency || !total || !canonicalAmount) throw new Error("blocked");
    const success = (offerId?: number | string | null): TransactionRecord => ({
      requestId,
      flow: "place-offer",
      outcome: "success",
      title: isFa ? "آفر ثبت شد" : "Offer placed",
      description: `${formatDecimalString(total, {
        locale: lang,
        maxFractionDigits: 18
      })} ${activeSymbol}`,
      // Only what the server actually returned. No id is fabricated.
      refs: offerId ? [{
        label: isFa ? "شناسه آفر" : "Offer ID",
        value: String(offerId),
        technical: true
      }] : undefined,
      at: new Date().toISOString()
    });
    const failure = (description?: string): TransactionRecord => ({
      requestId,
      flow: "place-offer",
      outcome: "failure",
      title: isFa ? "آفر ثبت نشد" : "Offer was not placed",
      description: description ?? (isFa ? "هیچ آفری ثبت نشد و مبلغی از موجودی شما کسر نشد. می‌توانید دوباره تلاش کنید." : "No offer was created and nothing was taken from your balance. You can try again."),
      at: new Date().toISOString()
    });

    // NOTHING BELOW MAY TURN A PLACED OFFER INTO A FAILED ONE — the decision lives in `submitPlaceOffer`.
    try {
      if (handoff.pending) {
        const pendingOfferId = handoff.pending.id;
        const result = await handoff.sign((offerId_0, payload) => confirmOfferStep(contractAddress, tokenId, offerId_0, payload));
        if (!result) return null;
        if (result.outcome === "pending_confirmation") return null;
        toast.success(isFa ? "آفر با موفقیت ثبت شد" : "Offer placed successfully");
        await refreshQuietly(onSuccess);
        return success(pendingOfferId);
      }
      const result_0 = await submitPlaceOffer({
        contractAddress,
        tokenId,
        payload: {
          currency_id: Number(currency.id),
          // Canonical, not the raw field value.
          total_price: Number(canonicalAmount),
          quantity: quantityNumber,
          deadline
        },
        onPlaced: onSuccess
      });
      if (result_0.outcome === "failure") {
        toastApiError(result_0.error, isFa ? "ثبت آفر ناموفق بود" : "Failed to place offer");
        // The receipt carries the server's own reason where it sent one.
        return failure(getErrorMessage(result_0.error, "") || undefined);
      }
      if (result_0.outcome === "sign_required") {
        // A self-custody wallet: nothing is placed until its rounds are done.
        const handed = handoff.receive(result_0.response, result_0.offerId);
        if (handed.kind === "handoff") return null;
      }
      if (result_0.outcome === "unusable" || result_0.outcome === "sign_required") {
        toast.error(isFa ? "ثبت آفر ناموفق بود" : "Failed to place offer");
        return failure(isFa ? "سرور برای این کار به امضای کیف پول نیاز داشت اما چیزی برای امضا نفرستاد." : "The server required a wallet signature but sent nothing to sign.");
      }
      if (classifyChainPost(result_0.response, "placeOffer") === "queued") {
        trackChainWork({
          mode: "placeOffer",
          title: assetName,
          image: assetImage,
          contractAddress,
          tokenId,
          lang,
          workId: result_0.offerId
        });
        toast.success(isFa ? "آفر ثبت شد؛ در انتظار بلاکچین" : "Offer submitted — waiting for the blockchain");
        return success(result_0.offerId);
      }
      toast.success(isFa ? "آفر با موفقیت ثبت شد" : "Offer placed successfully");
      return success(result_0.offerId);
    } catch (error) {
      toastApiError(error, isFa ? "ثبت آفر ناموفق بود" : "Failed to place offer");
      return failure(getErrorMessage(error, "") || undefined);
    }
  };
  const model: FlowModel = {
    lang,
    title: isFa ? "ثبت آفر" : "Make an offer",
    description: isFa ? "مبلغ و مدت اعتبار آفر را تعیین کنید." : "Set the offer amount and how long it stays open.",
    rows,
    irreversibility: isFa ? "اگر مالک این آفر را بپذیرد، انتقال انجام می‌شود و قابل برگشت نیست." : "If the owner accepts this offer, the transfer happens and cannot be reversed.",
    confirmLabel: handoff.continueLabel ?? (isFa ? "ثبت آفر" : "Place offer"),
    // The offers list below is the acknowledgement.
    closeOnSuccess: true,
    confirmConsequence: isFa ? "در صورت پذیرش، انتقال قابل برگشت نیست" : "if it is accepted, the transfer cannot be reversed",
    blockedReason: handoff.pending ? handoff.blockedReason : blockedReason,
    disableContinue: handoff.pending ? Boolean(handoff.blockedReason) : isBlocked,
    hasInput: true,
    handoff,
    networkLabel,
    onConfirm,
    onRecord,
    onClose: handoff.guardClose(onClose)
  };
  return {
    model,
    amount,
    setAmount,
    touchAmount: () => setAmountTouched(true),
    amountError,
    quantity,
    setQuantity,
    quantityError,
    duration,
    setDuration,
    selectedCurrencyId,
    setSelectedCurrencyId,
    currencies,
    currencyReason: currencyStateReason(currencyState, isFa),
    symbol: activeSymbol,
    /** Set when the offer is more than the wallet can spend — the dialog shows the top-up beside it. */
    shortfall: short && spend && canonicalAmount ? {
      need: canonicalAmount,
      have: spend.amount,
      symbol: activeSymbol ?? "",
      walletId: spend.walletId
    } : null
  };
}